import React, { useCallback, useEffect, useState } from "react";
import { Sparkles, RefreshCw, X, TrendingUp, TrendingDown, Minus, AlertTriangle } from "lucide-react";
import { pairUniverse } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Chart uses OANDA spot for metals; Yahoo only has futures — send a spot hint so
// the backend returns the exact spot price/levels that match the chart.
const SPOT_HINT = { "OANDA:XAUUSD": "XAU", "OANDA:XAGUSD": "XAG" };

const fmt = (v, d) => (v == null ? "—" : Number(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }));

const Skeleton = () => (
  <div className="space-y-3 animate-pulse" data-testid="ws-ai-skeleton">
    <div className="h-24 rounded-xl bg-[rgba(120,140,180,0.10)]" />
    {[...Array(5)].map((_, i) => <div key={i} className="h-6 rounded bg-[rgba(120,140,180,0.08)]" />)}
    <div className="h-16 rounded-xl bg-[rgba(120,140,180,0.10)]" />
    <div className="h-16 rounded-xl bg-[rgba(120,140,180,0.10)]" />
  </div>
);

const Section = ({ title, children }) => (
  <div className="mt-4">
    <div className="text-[10px] font-semibold tracking-[0.22em] text-[color:var(--ws-muted)] uppercase mb-2">{title}</div>
    {children}
  </div>
);

const WSAIView = ({ pane, onClose }) => {
  const meta = pairUniverse.find((p) => p.tv === pane?.symbol);
  const yf = meta?.id;
  const label = meta?.label || pane?.symbol || "—";
  const interval = pane?.interval || "60";

  const [state, setState] = useState({ loading: true, error: null, data: null });

  const run = useCallback(async (nocache = false) => {
    if (!yf) { setState({ loading: false, error: "unsupported", data: null }); return; }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await fetch(`${API}/ai/chart-analysis`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ yf, label, interval, nocache, spot: SPOT_HINT[pane?.symbol] }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error("failed");
      setState({ loading: false, error: null, data });
    } catch {
      setState({ loading: false, error: "failed", data: null });
    }
  }, [yf, label, interval, pane?.symbol]);

  useEffect(() => { run(false); }, [run]);

  const d = state.data;
  const bias = d?.bias || "NEUTRAL";
  const biasCls = bias === "BULLISH" ? "ws-up" : bias === "BEARISH" ? "ws-down" : "text-[color:var(--ws-muted)]";
  const BiasIcon = bias === "BULLISH" ? TrendingUp : bias === "BEARISH" ? TrendingDown : Minus;
  const dec = d?.decimals ?? 2;

  return (
    <aside
      className="ws-panel p-0 w-full lg:w-[380px] shrink-0 flex flex-col overflow-hidden lg:h-[calc(100vh-200px)]"
      style={{ minHeight: 420 }}
      data-testid="ws-ai-view-panel"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 px-5 pt-4 pb-3 border-b border-[color:var(--ws-border)]">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[color:var(--ws-cyan)]" />
            <h3 className="ws-heading text-[14px] font-semibold tracking-wide">WEALTH SECRET AI VIEW</h3>
          </div>
          <div className="text-[12px] text-[color:var(--ws-muted)] mt-0.5">
            {label} · {d?.timeframe || interval}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => run(true)} disabled={state.loading} title="Refresh analysis" data-testid="ws-ai-refresh"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-[color:var(--ws-muted)] hover:text-white hover:bg-[rgba(255,255,255,0.06)] transition-colors disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${state.loading ? "animate-spin" : ""}`} />
          </button>
          <button onClick={onClose} title="Close" data-testid="ws-ai-close"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-[color:var(--ws-muted)] hover:text-white hover:bg-[rgba(255,255,255,0.06)] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {state.loading ? (
          <Skeleton />
        ) : state.error === "unsupported" ? (
          <div className="text-[13px] text-[color:var(--ws-muted)] py-8 text-center">
            Analysis isn't available for this symbol yet.
          </div>
        ) : state.error ? (
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <AlertTriangle className="w-6 h-6 ws-down" />
            <div className="text-[13px] text-[color:var(--ws-muted)]">Couldn't load the analysis.</div>
            <button onClick={() => run(true)} className="ws-btn-primary px-4 py-2 rounded-lg text-[12px] font-semibold inline-flex items-center gap-2" data-testid="ws-ai-retry">
              <RefreshCw className="w-3.5 h-3.5" /> Retry
            </button>
          </div>
        ) : d && d.insufficient ? (
          <div className="text-[13px] text-[color:var(--ws-muted)] py-8 text-center">Insufficient data for a reliable read.</div>
        ) : d ? (
          <>
            {/* Market bias */}
            <div className="rounded-xl p-4 text-center" style={{ background: "rgba(56,189,248,0.05)", border: "1px solid var(--ws-border)" }} data-testid="ws-ai-bias">
              <div className="text-[10px] tracking-[0.22em] text-[color:var(--ws-muted)] uppercase mb-1.5">Market Bias</div>
              <div className={`ws-heading text-[24px] font-bold inline-flex items-center gap-2 ${biasCls}`}>
                <BiasIcon className="w-5 h-5" /> {bias}
              </div>
              <div className="flex items-center justify-center gap-4 mt-2 text-[12px]">
                <span className="text-[color:var(--ws-muted)]">Trend: <span className="text-white">{d.trend || "—"}</span></span>
                <span className="text-[color:var(--ws-muted)]">Confidence: <span className="text-white">{d.confidence || "—"}</span></span>
              </div>
            </div>

            {/* Key levels */}
            <Section title="Key Levels">
              <div className="space-y-1.5">
                {(d.resistances || []).map((r, i) => (
                  <div key={`r${i}`} className="flex items-center justify-between text-[13px] px-3 py-1.5 rounded-lg" style={{ background: "rgba(248,113,113,0.06)" }}>
                    <span className="text-[color:var(--ws-muted)]">Resistance</span>
                    <span className="ws-heading font-semibold ws-down tabular-nums">{fmt(r, dec)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between text-[13px] px-3 py-1.5 rounded-lg" style={{ background: "rgba(56,189,248,0.10)", border: "1px solid rgba(56,189,248,0.25)" }}>
                  <span className="text-white font-medium">Current</span>
                  <span className="ws-heading font-bold text-[color:var(--ws-cyan)] tabular-nums">{fmt(d.current, dec)}</span>
                </div>
                {(d.supports || []).map((s, i) => (
                  <div key={`s${i}`} className="flex items-center justify-between text-[13px] px-3 py-1.5 rounded-lg" style={{ background: "rgba(52,211,153,0.06)" }}>
                    <span className="text-[color:var(--ws-muted)]">Support</span>
                    <span className="ws-heading font-semibold ws-up tabular-nums">{fmt(s, dec)}</span>
                  </div>
                ))}
              </div>
            </Section>

            {/* Market structure */}
            <Section title="Market Structure">
              <div className="ws-price-cell p-3">
                <div className="ws-heading text-[15px] font-semibold text-white tracking-wide">{d.structure_sequence || "—"}</div>
                <div className="text-[12px] text-[color:var(--ws-muted)] mt-0.5">Structure: <span className="text-white">{d.structure_label || "—"}</span></div>
              </div>
            </Section>

            {/* Price action */}
            <Section title="Price Action">
              <p className="text-[13px] text-[color:var(--ws-text)] leading-relaxed opacity-90">{d.price_action || "—"}</p>
            </Section>

            {/* Scenarios */}
            <Section title="Scenarios">
              <div className="space-y-2">
                <div className="rounded-lg p-3" style={{ background: "rgba(52,211,153,0.06)", border: "1px solid rgba(52,211,153,0.18)" }}>
                  <div className="text-[12px] font-semibold ws-up mb-0.5">🟢 Bullish</div>
                  <div className="text-[12px] text-[color:var(--ws-text)] opacity-90">{d.scenario_bull || "—"}</div>
                </div>
                <div className="rounded-lg p-3" style={{ background: "rgba(248,113,113,0.06)", border: "1px solid rgba(248,113,113,0.18)" }}>
                  <div className="text-[12px] font-semibold ws-down mb-0.5">🔴 Bearish</div>
                  <div className="text-[12px] text-[color:var(--ws-text)] opacity-90">{d.scenario_bear || "—"}</div>
                </div>
              </div>
            </Section>

            {/* WS view */}
            <Section title="Wealth Secret View">
              <div className="rounded-xl p-3.5" style={{ background: "linear-gradient(135deg, rgba(37,99,235,0.12), rgba(56,189,248,0.06))", border: "1px solid rgba(56,189,248,0.25)" }}>
                <p className="text-[13px] text-white leading-relaxed">{d.ws_view || "—"}</p>
              </div>
            </Section>

            <p className="text-[10px] text-[color:var(--ws-muted)] mt-4 opacity-70">AI-generated analysis of live data. Not financial advice; markets carry risk.</p>
          </>
        ) : null}
      </div>
    </aside>
  );
};

export default WSAIView;
